import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TuiActiveZoneModule, TuiLetModule } from '@taiga-ui/cdk';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  delay,
  filter,
  map,
  merge,
  of,
  skip,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AsyncPipe, NgForOf } from '@angular/common';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import {
  TuiDataListDropdownManagerModule,
  TuiInputModule,
  TuiInputNumberModule,
  TuiToggleModule,
} from '@taiga-ui/kit';
import { fromFormControlValue } from '@shared/utils/observables';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import {
  TuiButtonModule,
  TuiDataListModule,
  TuiDialogService,
  TuiDropdownModule,
  TuiGroupModule,
  TuiHintModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import CatalogsState from '@store/catalogs/catalogs.state';
import { Store } from '@ngxs/store';
import { CargoPlaceType } from '@shared/constants/cargo-places';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@shared/services/alert.service';
import { FormsService } from '@shared/services/forms.service';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import {
  BuyerFormComponent,
  BuyerFormInputData,
} from '@shared/components/catalogs/buyer-form/buyer-form.component';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { AuthState } from '@store/auth/auth.state';
import {
  ClientFormComponent,
  ClientFormInputData,
} from '@shared/components/catalogs/client-form/client-form.component';
import {
  FarmFormComponent,
  FarmFormInputData,
} from '@shared/components/catalogs/farm-form/farm-form.component';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import { CURRENCIES } from '@shared/constants/currency';
import {
  PreOrder,
  PreOrderCargoPlaceDetailsForm,
  PreOrderForm,
} from '../../../../types/pre-order';
import { PreOrderService } from '../../services/pre-order.service';
import { CommonStateService } from '../../services/common-state.service';
import { CargoPlaceDetailsComponent } from './components/cargo-place-details/cargo-place-details.component';
import { ClientJson } from '../../../../types/cargo';
import { Farm } from '../../../../types/farm';

@Component({
  selector: 'app-pre-order',
  standalone: true,
  imports: [
    TuiActiveZoneModule,
    AsyncPipe,
    AutocompleteComponent,
    TuiToggleModule,
    ReactiveFormsModule,
    CheckboxComponent,
    TuiGroupModule,
    TuiInputModule,
    TuiInputNumberModule,
    TuiTextfieldControllerModule,
    TuiHintModule,
    TuiDropdownModule,
    TuiDataListDropdownManagerModule,
    TuiDataListModule,
    CargoPlaceDetailsComponent,
    TuiButtonModule,
    TuiLetModule,
    NgForOf,
    InputDateComponent,
  ],
  templateUrl: './pre-order.component.html',
  styleUrl: './pre-order.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreOrderComponent implements OnInit {
  isGreenlandUser = this.store.selectSignal(AuthState.isGreenlandUser);

  update$ = new Subject<void>();
  isActiveZone$ = new BehaviorSubject(false);
  openClientDialog$ = new Subject<'create' | 'edit'>();
  openCreateBuyerDialog$ = new Subject<void>();
  openFarmDialog$ = new Subject<'create' | 'edit'>();

  preOrderForm = this.fb.group<PreOrderForm>({
    uniqueKey: this.fb.control(null),
    attachmentId: this.fb.control(null),
    dataSentTo1C: this.fb.control(null),
    invoiceCost: this.fb.control(null),
    invoiceCurrencyLetter: this.fb.control(null),
    invoiceDate: this.fb.control(null),
    invoiceNumber: this.fb.control(null),
    brokerJson: this.forms.getSellerJsonForm(),
    expectedLoadingDate: this.fb.control(null),
    paymentDate: this.fb.control(null),
    prepayment: this.fb.control(null),
    prepaymentSum: this.fb.control(null),
    prepaymentCurrencyLetter: this.fb.control(null),
    id: this.fb.control<Nullable<number>>(null),
    cargoId: this.fb.control<Nullable<number>>(null),
    countryLetter: this.fb.control(''),
    airWaybillNumber: this.fb.control(''),
    precooling: this.fb.control<Nullable<boolean>>(null),
    weight: this.fb.control<Nullable<number>>(null),
    buyerCode: this.fb.control(''),
    farmJson: this.forms.getFarmJsonForm(),
    farmBrokerJson: this.forms.getFarmJsonForm(),
    farmAddress: this.forms.getFarmAddressForm(),
    clientJson: this.forms.getClientJsonForm(),
    flowerType: this.fb.control(''),
    imported: this.fb.control<Nullable<boolean>>({
      value: null,
      disabled: true,
    }),
    cold: this.fb.control<Nullable<boolean>>(null),
    clientAddressWithCoordinatesJson:
      this.forms.getClientAddressWithCoordinatesJson(),
    cargoPlacesJson: this.fb.array<FormGroup<PreOrderCargoPlaceDetailsForm>>(
      [],
    ),
    divisionId: this.fb.control(null),
    userId: this.fb.nonNullable.control(0),
  });

  isImport$ = fromFormControlValue(this.preOrderForm.controls.imported);

  availableAddresses$ =
    this.addressesWithCoordinatesJsonControl.valueChanges.pipe(
      startWith([]),
      map(() => this.addressesWithCoordinatesJsonControl.getRawValue()),
    );

  availableFarmAddresses$ = fromFormControlValue(
    this.preOrderForm.controls.farmJson.controls.addressesJson,
  );

  availableFlowerTypes$ = combineLatest([
    this.catalogs.flowerTypes$,
    fromFormControlValue(
      this.preOrderForm.controls.farmJson.controls.flowerTypesJson,
    ),
  ]).pipe(
    map(([flowerTypes, farmFlowerTypeIds]) => {
      if (
        !flowerTypes.length ||
        !farmFlowerTypeIds ||
        !farmFlowerTypeIds.length
      ) {
        return [];
      }
      return flowerTypes.filter(flowerType => {
        return farmFlowerTypeIds.includes(flowerType.id.toString());
      });
    }),
  );

  sellers$ = this.catalogs.sellers$;

  buyersOfSelectedFarm$ = combineLatest([
    fromFormControlValue(this.preOrderForm.controls.farmJson.controls.id),
    fromFormControlValue(this.preOrderForm.controls.clientJson.controls.id),
    this.catalogs.buyers$,
  ]).pipe(
    map(([farmId, clientId, buyers]) => {
      if (!farmId || !clientId) {
        return [];
      }
      return buyers.filter(
        buyer => buyer.farmId === farmId && buyer.clientId === clientId,
      );
    }),
  );

  countries = this.store.selectSignal(CatalogsState.getNsiCountries);

  protected readonly CURRENCIES = CURRENCIES;
  sub?: Subscription;
  constructor(
    protected readonly catalogs: CargoCatalogsService,
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly alert: AlertService,
    private readonly preOrderService: PreOrderService,
    private readonly destroyRef: DestroyRef,
    private readonly commonState: CommonStateService,
    private readonly forms: FormsService,
    private readonly dialogs: TuiDialogService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  onFarmAddressChange(address: Nullable<string>) {
    this.setFarmAddressDetails(address);
  }

  onFarmChange() {
    const id =
      this.preOrderForm.controls.farmJson.controls.id.value?.toString() ?? '';
    this.setFarmDetails(id);
    this.clearFarmAddress();
    this.clearClientCodes();
    this.setDefaultCargoPlaceIfImport();
    this.fillFarmAddressIfSingle(this.farmJson.getRawValue());
  }

  onFarmBrokerChange() {
    const id =
      this.preOrderForm.controls.farmBrokerJson.controls.id.value?.toString() ??
      '';
    this.setFarmBrokerDetails(id);
  }

  onClientChange() {
    const id =
      this.preOrderForm.controls.clientJson.controls.id.value?.toString() ?? '';
    this.setClientName(id);
    this.clearClientCodes();
    this.clearClientAddress();
    this.fillClientAddressIfSingle(this.clientJson.getRawValue());
  }

  onAddressChange() {
    const address =
      this.clientAddressWithCoordinatesJson.controls.address.value?.toString() ??
      '';

    if (!address) {
      this.clientAddressWithCoordinatesJson.reset();
    } else {
      const addresses = this.addressesWithCoordinatesJsonControl.getRawValue();
      const details = addresses.find(a => a.address === address);
      if (details) {
        this.clientAddressWithCoordinatesJson.patchValue(details);
      } else {
        this.clientAddressWithCoordinatesJson.reset();
      }
    }
    this.clientAddressWithCoordinatesJson.markAsDirty();
  }

  onZoneChange(focused: boolean) {
    if (!focused) {
      this.update$.next();
    }
    this.isActiveZone$.next(focused);
  }

  onUpdate() {
    this.update$
      .pipe(
        delay(0),
        filter(() => !this.preOrderForm.pristine),
        switchMap(() =>
          this.preOrderService.update(
            this.preOrderForm.getRawValue() as PreOrder,
          ),
        ),
        tap(() => {
          this.alert.showSuccess();
          this.preOrderForm.markAsUntouched();
          this.preOrderForm.markAsPristine();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onCreateBuyerClick() {
    this.openCreateBuyerDialog$
      .pipe(
        switchMap(() => {
          const data: BuyerFormInputData = {
            buyer: {
              id: null,
              uniqueKey: null,
              clientId: this.clientJson.controls.id.value,
              farmId: this.farmJson.controls.id.value,
              code: this.preOrderForm.controls.buyerCode.value,
              name: null,
              publicAccess: null,
            },
          };
          return this.dialogs.open(
            new PolymorpheusComponent(BuyerFormComponent),
            {
              dismissible: false,
              closeable: false,
              size: 'auto',
              data,
            },
          );
        }),
        tap(() => this.catalogs.fetchBuyers$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenClientDialogClick() {
    this.openClientDialog$
      .pipe(
        switchMap(type => {
          return this.dialogs.open(
            new PolymorpheusComponent(ClientFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data: type === 'edit' ? this.getClientFormInputData() : undefined,
            },
          );
        }),
        concatMap(() => {
          const nextClientUpdate$ = this.catalogs.clients$.pipe(
            skip(1),
            take(1),
          );

          this.catalogs.fetchClients$.next();

          return nextClientUpdate$;
        }),
        tap(() => this.syncAfterClientCatalogUpdate()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenFarmDialogClick() {
    this.openFarmDialog$
      .pipe(
        switchMap(type => {
          return this.dialogs.open(
            new PolymorpheusComponent(FarmFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data: type === 'edit' ? this.getFarmFormInputData() : undefined,
            },
          );
        }),
        tap(() => this.catalogs.fetchFarms$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  syncAfterClientCatalogUpdate() {
    const id =
      this.preOrderForm.controls.clientJson.controls.id.value?.toString() ?? '';
    const currentAddress =
      this.preOrderForm.controls.clientAddressWithCoordinatesJson.controls.address.getRawValue();

    this.setClientName(id);

    const updatedClient = this.catalogs.clientMap.get(id.toString());

    const sameAddress = updatedClient?.addressesWithCoordinatesJson.find(
      a => a.address === currentAddress,
    );

    if (sameAddress) {
      this.preOrderForm.controls.clientAddressWithCoordinatesJson.setValue(
        sameAddress,
      );
    } else {
      this.clearClientAddress();
    }
  }

  fillBrokerJson(sellerId: Nullable<string>) {
    const sellerJsonControl = this.preOrderForm.controls.brokerJson;
    const seller =
      this.catalogs.sellerMap.get(sellerId?.toString() ?? '') ?? null;
    if (seller) {
      sellerJsonControl.patchValue(seller);
    } else {
      sellerJsonControl.reset();
      sellerJsonControl.markAsDirty();
    }
  }

  fillClientAddressIfSingle(client: ClientJson) {
    if (client.addressesWithCoordinatesJson.length) {
      const [addressesWithCoordinatesJson] =
        client.addressesWithCoordinatesJson;
      this.preOrderForm.controls.clientAddressWithCoordinatesJson.setValue(
        addressesWithCoordinatesJson,
      );
    }
  }

  fillFarmAddressIfSingle(farm: Farm) {
    if (farm.addressesJson.length) {
      const [addressesJson] = farm.addressesJson;
      this.preOrderForm.controls.farmAddress.setValue(addressesJson);
    }
  }

  getClientFormInputData(): ClientFormInputData | undefined {
    const { id } = this.preOrderForm.controls.clientJson.value;
    const clientFromCatalog = id
      ? this.catalogs.clientMap.get(id.toString())
      : undefined;

    return {
      client: clientFromCatalog,
    };
  }

  getFarmFormInputData(): FarmFormInputData | undefined {
    const { id } = this.preOrderForm.controls.farmJson.value;
    const farmFromCatalog = id
      ? this.catalogs.farmMap.get(id.toString())
      : undefined;

    return {
      farm: farmFromCatalog,
    };
  }

  clearFarmAddress() {
    this.farmAddress.reset();
  }

  get clientAddressWithCoordinatesJson() {
    return this.preOrderForm.controls.clientAddressWithCoordinatesJson;
  }

  get addressesWithCoordinatesJsonControl() {
    return this.preOrderForm.controls.clientJson.controls
      .addressesWithCoordinatesJson;
  }

  clearClientAddress() {
    this.preOrderForm.controls.clientAddressWithCoordinatesJson.reset();
  }

  get farmJson() {
    return this.preOrderForm.controls.farmJson;
  }

  get farmBrokerJson() {
    return this.preOrderForm.controls.farmBrokerJson;
  }

  get farmAddress() {
    return this.preOrderForm.controls.farmAddress;
  }

  get clientJson() {
    return this.preOrderForm.controls.clientJson;
  }

  setFarmAddressDetails(addressJson: Nullable<string>) {
    if (!addressJson) {
      this.farmAddress.reset();
    } else {
      const farmAddress = this.farmJson.controls.addressesJson.value.find(
        a => a.address === addressJson,
      );
      farmAddress
        ? this.farmAddress.patchValue(farmAddress)
        : this.farmAddress.reset();
    }
    this.farmAddress.markAsDirty();
  }

  setFarmBrokerDetails(farmId: Nullable<string>) {
    if (!farmId) {
      this.farmBrokerJson.reset();
    } else {
      const farm = this.catalogs.farmMap.get(farmId.toString());
      if (farm) {
        this.farmBrokerJson.patchValue(farm);
      } else {
        this.farmBrokerJson.reset();
      }
    }
    this.farmBrokerJson.markAsDirty();
  }

  setFarmDetails(farmId: Nullable<string>) {
    if (!farmId) {
      this.preOrderForm.patchValue({
        flowerType: null,
        imported: null,
      });
      this.farmJson.reset();
    } else {
      const farm = this.catalogs.farmMap.get(farmId.toString());
      if (farm) {
        this.farmJson.patchValue(farm);
        this.preOrderForm.patchValue({
          flowerType: null,
          imported: farm.imported,
        });
      } else {
        this.farmJson.reset();
      }
    }
    this.farmJson.markAsDirty();
  }

  setDefaultCargoPlaceIfImport() {
    if (this.preOrderForm.controls.imported.getRawValue()) {
      const cargoPlace = this.catalogs.cargoPlaceMap.get(
        CargoPlaceType.PALLET_US,
      );

      this.preOrderForm.controls.cargoPlacesJson.controls.forEach(details => {
        if (cargoPlace) {
          details.controls.cargoPlaceJson.patchValue(cargoPlace);
        } else {
          details.controls.cargoPlaceJson.reset();
        }
      });
    }
  }

  setClientName(id: Nullable<string>) {
    if (!id) {
      this.clientJson.reset();
    } else {
      const client = this.catalogs.clientMap.get(id.toString());
      if (client) {
        this.clientJson.patchValue(client);
      } else {
        this.clientJson.reset();
      }
    }
    this.clientJson.markAsDirty();
  }

  clearClientCodes() {
    this.preOrderForm.controls.buyerCode.setValue(null);
  }

  @Input({ required: true }) set preOrder(preOrder: PreOrder) {
    this.sub?.unsubscribe();
    const { cargoPlacesJson, ...rest } = preOrder;
    this.preOrderForm.patchValue(preOrder, { emitEvent: false });
    const cargoPlacesJsonControl = this.preOrderForm.controls.cargoPlacesJson;
    cargoPlacesJsonControl.clear({ emitEvent: false });
    cargoPlacesJson.forEach((details, index, array) => {
      const form = this.forms.getPreOrderCargoPlaceDetailsForm(details);
      form.controls.packageQuantityLimit.disable({ emitEvent: false });
      cargoPlacesJsonControl.push(form, {
        emitEvent: index === array.length - 1,
      });
    });

    this.sub = merge(
      this.disablePrepaymentDetails(this.preOrderForm),
      this.clearPrepaymentDetails(this.preOrderForm),
    ).subscribe();
  }

  removeCargoPlaceDetails(index: number) {
    this.preOrderForm.controls.cargoPlacesJson.removeAt(index);
    this.preOrderForm.markAsTouched();
    this.preOrderForm.markAsDirty();
  }

  addCargoPlaceDetails() {
    const form = this.forms.getPreOrderCargoPlaceDetailsForm();
    form.controls.packageQuantityLimit.disable({ emitEvent: false });
    if (this.preOrderForm.controls.imported.value) {
      const cargoPlace = this.catalogs.cargoPlaceMap.get(
        CargoPlaceType.PALLET_US,
      );
      if (cargoPlace) {
        form.controls.cargoPlaceJson.patchValue(cargoPlace);
      }
    }
    this.preOrderForm.controls.cargoPlacesJson.push(form);
    this.preOrderForm.markAsTouched();
    this.preOrderForm.markAsDirty();
  }

  clearPrepaymentDetails(form: FormGroup<PreOrderForm>) {
    return form.controls.prepayment.valueChanges.pipe(
      tap(prepayment => {
        if (!prepayment) {
          form.patchValue({
            prepaymentCurrencyLetter: null,
            prepaymentSum: null,
            paymentDate: null,
          });
        }
      }),
    );
  }

  disablePrepaymentDetails(form: FormGroup<PreOrderForm>) {
    return fromFormControlValue(form.controls.prepayment).pipe(
      tap(prepayment => {
        const controls = form.controls;
        if (prepayment) {
          controls.prepaymentSum.enable();
          controls.prepaymentCurrencyLetter.enable();
          controls.paymentDate.enable();
        } else {
          controls.prepaymentSum.disable();
          controls.prepaymentCurrencyLetter.disable();
          controls.paymentDate.disable();
        }
      }),
    );
  }

  ngOnInit() {
    this.onUpdate();
    this.onCreateBuyerClick();
    this.onOpenClientDialogClick();
    this.onOpenFarmDialogClick();
  }
}
